import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faChartBar,
  faClock,
  faCrown,
  faDownload,
  faEuroSign,
  faEye,
  faFilter,
  faGift,
  faHeart,
  faSearch,
  faStar,
  faUserAlt,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';

const CustomerAnalysisPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [filterSegment, setFilterSegment] = useState('all');

  const breadcrumb = [{ label: 'Reporty' }, { label: 'Zákaznická analýza' }];

  const customerSegments = [
    { id: 'vip', name: 'VIP zákazníci', color: 'bg-yellow-100 text-yellow-800', icon: faCrown },
    { id: 'regular', name: 'Pravidelní', color: 'bg-blue-100 text-blue-800', icon: faUsers },
    { id: 'new', name: 'Noví', color: 'bg-green-100 text-green-800', icon: faHeart },
    { id: 'inactive', name: 'Neaktivní', color: 'bg-gray-100 text-gray-800', icon: faClock },
  ];

  const customerData = [
    {
      id: '1',
      name: 'Anna Nováková',
      email: 'anna.novakova@email.cz',
      phone: '+420 123 456 789',
      segment: 'vip',
      totalVisits: 24,
      totalSpent: 19200,
      averageSpent: 800,
      lastVisit: '2024-11-15',
      firstVisit: '2023-06-10',
      favoriteService: 'Střih a foukaná',
      loyaltyPoints: 1250,
      satisfaction: 4.9,
      referrals: 5,
    },
    {
      id: '2',
      name: 'Petr Svoboda',
      email: 'petr.svoboda@email.cz',
      phone: '+420 987 654 321',
      segment: 'regular',
      totalVisits: 18,
      totalSpent: 10800,
      averageSpent: 600,
      lastVisit: '2024-11-14',
      firstVisit: '2023-08-15',
      favoriteService: 'Holení',
      loyaltyPoints: 540,
      satisfaction: 4.6,
      referrals: 2,
    },
    {
      id: '3',
      name: 'Marie Krásná',
      email: 'marie.krasna@email.cz',
      phone: '+420 555 666 777',
      segment: 'regular',
      totalVisits: 15,
      totalSpent: 18000,
      averageSpent: 1200,
      lastVisit: '2024-11-12',
      firstVisit: '2024-01-20',
      favoriteService: 'Barvení vlasů',
      loyaltyPoints: 900,
      satisfaction: 4.8,
      referrals: 3,
    },
    {
      id: '4',
      name: 'Jana Světlá',
      email: 'jana.svetla@email.cz',
      phone: '+420 111 222 333',
      segment: 'new',
      totalVisits: 3,
      totalSpent: 2400,
      averageSpent: 800,
      lastVisit: '2024-11-10',
      firstVisit: '2024-10-15',
      favoriteService: 'Kosmetické ošetření',
      loyaltyPoints: 120,
      satisfaction: 4.7,
      referrals: 0,
    },
    {
      id: '5',
      name: 'Tomáš Černý',
      email: 'tomas.cerny@email.cz',
      phone: '+420 444 555 666',
      segment: 'inactive',
      totalVisits: 8,
      totalSpent: 4800,
      averageSpent: 600,
      lastVisit: '2024-08-20',
      firstVisit: '2023-12-01',
      favoriteService: 'Střih',
      loyaltyPoints: 240,
      satisfaction: 4.3,
      referrals: 1,
    },
  ];

  const analytics = {
    totalCustomers: 198,
    newCustomers: 23,
    returningCustomers: 175,
    averageCustomerValue: 1850,
    customerRetentionRate: 78.5,
    averageVisitsPerCustomer: 8.2,
    topSpendingSegment: 'VIP zákazníci',
    mostPopularService: 'Střih a foukaná',
  };

  const filteredCustomers = customerData.filter(customer => filterSegment === 'all' || customer.segment === filterSegment);

  const getSegmentInfo = (segmentId: string) => {
    return customerSegments.find(s => s.id === segmentId) || customerSegments[0];
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FontAwesomeIcon key={i} icon={faStar} className={`w-4 h-4 ${i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}`} />
    ));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ');
  };

  const getDaysSinceLastVisit = (dateString: string) => {
    const lastVisit = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - lastVisit.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Zákaznická analýza" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Zákaznická analýza</h1>
                <p className="text-gray-600 mt-1">Detailní analýza zákazníků a jejich chování</p>
              </div>
              <div className="flex items-center space-x-3">
                <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="week">Tento týden</option>
                  <option value="month">Tento měsíc</option>
                  <option value="quarter">Toto čtvrtletí</option>
                  <option value="year">Tento rok</option>
                </select>
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Analytics Overview */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faUsers} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem zákazníků</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.totalCustomers}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faHeart} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Noví zákazníci</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.newCustomers}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faEuroSign} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Průměrná hodnota</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.averageCustomerValue} Kč</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <FontAwesomeIcon icon={faChartBar} className="text-xl text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Retence</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.customerRetentionRate}%</p>
                </div>
              </div>
            </div>
          </section>

          {/* Customer Segments */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Zákaznické segmenty</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {customerSegments.map(segment => {
                const count = customerData.filter(c => c.segment === segment.id).length;
                const percentage = Math.round((count / customerData.length) * 100);

                return (
                  <div key={segment.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`p-2 rounded-lg ${segment.color}`}>
                        <FontAwesomeIcon icon={segment.icon} className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{segment.name}</h4>
                        <p className="text-2xl font-bold text-gray-900">{count}</p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">{percentage}% z celku</div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Filters */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat zákazníky..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <select
                value={filterSegment}
                onChange={e => setFilterSegment(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="all">Všechny segmenty</option>
                {customerSegments.map(segment => (
                  <option key={segment.id} value={segment.id}>
                    {segment.name}
                  </option>
                ))}
              </select>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                Více filtrů
              </button>
            </div>
          </section>

          {/* Customer Analysis Table */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zákazník</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Segment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Návštěvy</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Celkem utraceno</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Poslední návštěva</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Spokojenost</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loajální body</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCustomers.map(customer => {
                    const segmentInfo = getSegmentInfo(customer.segment);
                    const daysSinceLastVisit = getDaysSinceLastVisit(customer.lastVisit);

                    return (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <FontAwesomeIcon icon={faUserAlt} className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                              <div className="text-sm text-gray-500">{customer.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${segmentInfo.color}`}>
                            <FontAwesomeIcon icon={segmentInfo.icon} className="w-3 h-3 mr-1" />
                            {segmentInfo.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{customer.totalVisits}</div>
                          <div className="text-sm text-gray-500">návštěv</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{customer.totalSpent.toLocaleString()} Kč</div>
                          <div className="text-sm text-gray-500">Průměr: {customer.averageSpent} Kč</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDate(customer.lastVisit)}</div>
                          <div className={`text-sm ${daysSinceLastVisit > 90 ? 'text-red-500' : daysSinceLastVisit > 30 ? 'text-yellow-500' : 'text-green-500'}`}>
                            {daysSinceLastVisit} dní zpět
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className="flex">{renderStars(customer.satisfaction)}</div>
                            <span className="text-sm text-gray-900">{customer.satisfaction}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faGift} className="w-4 h-4 mr-1 text-purple-500" />
                            <span className="text-sm font-medium text-gray-900">{customer.loyaltyPoints}</span>
                          </div>
                          <div className="text-xs text-gray-500">{customer.referrals} doporučení</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 p-1" title="Zobrazit detail">
                            <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Customer Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Customers */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Nejlepší zákazníci</h3>
              <div className="space-y-4">
                {customerData
                  .sort((a, b) => b.totalSpent - a.totalSpent)
                  .slice(0, 5)
                  .map(customer => {
                    const segmentInfo = getSegmentInfo(customer.segment);
                    return (
                      <div key={customer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                            <FontAwesomeIcon icon={faCrown} className="w-4 h-4 text-yellow-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{customer.name}</p>
                            <p className="text-sm text-gray-600">{customer.totalVisits} návštěv</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{customer.totalSpent.toLocaleString()} Kč</p>
                          <p className="text-xs text-gray-500">{segmentInfo.name}</p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </section>

            {/* Customer Behavior */}
            <section className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Zákaznické chování</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faCalendar} className="w-5 h-5 text-blue-500 mr-3" />
                    <span className="text-sm text-gray-700">Průměrné návštěvy na zákazníka</span>
                  </div>
                  <span className="font-bold text-gray-900">{analytics.averageVisitsPerCustomer}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faHeart} className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-sm text-gray-700">Nejpopulárnější služba</span>
                  </div>
                  <span className="font-bold text-gray-900">{analytics.mostPopularService}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faCrown} className="w-5 h-5 text-yellow-500 mr-3" />
                    <span className="text-sm text-gray-700">Nejvýnosnější segment</span>
                  </div>
                  <span className="font-bold text-gray-900">{analytics.topSpendingSegment}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faUsers} className="w-5 h-5 text-purple-500 mr-3" />
                    <span className="text-sm text-gray-700">Retence zákazníků</span>
                  </div>
                  <span className="font-bold text-gray-900">{analytics.customerRetentionRate}%</span>
                </div>
              </div>
            </section>
          </div>

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faGift} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Loyalty program</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export dat</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faChartBar} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Segmentace</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faHeart} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Kampaně</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CustomerAnalysisPage;
